import React from 'react'

import {FormikProps} from 'formik'
import capitalize from 'lodash/capitalize'
import compact from 'lodash/compact'
import * as Yup from 'yup'

import useKeyPress from '../../../../../commons/src/hooks/use-key-press'
import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import {formatCurrency} from '../../../libs/formatter'
import {
  PossiblyMixedValue,
  isMixedValue,
  toMixedValidationSchema,
  unlockMixedValuesLabel,
} from '../../utils/form'

import {DefaultProductionTimes, ProductionTimeOption, Provider} from '../../../types'

import FieldNote from '../../../../../commons/src/components/field-note'
import FormRow from '../../../../../commons/src/components/form-row'
import Headline from '../../../../../commons/src/components/headline'
import InputField from '../../../../../commons/src/components/input-field'
import NumberInputField from '../../../../../commons/src/components/number-input-field'
import Paragraph from '../../../../../commons/src/components/paragraph'
import SelectField from '../../../../../commons/src/components/select-field'
import SelectMenu from '../../../../../commons/src/components/select-menu'
import MixedValueFormSection from '../../../components/mixed-value-form-section'
import MixedValueInputWrapper from '../../../components/mixed-value-input-wrapper/mixed-value-input-wrapper'

type ProductionTimeOptionOption = {
  label: string
  value: ProductionTimeOption
}

export type SingleFormData = {
  name: string
  type: string
  basePrice: string
  productionTimeFastTotal: string
  productionTimeSlowTotal: string
  factor: string
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

export type MixedFormData = {
  name: PossiblyMixedValue<string>
  type: PossiblyMixedValue<string>
  basePrice: PossiblyMixedValue<string>
  productionTimeFastTotal: PossiblyMixedValue<string>
  productionTimeSlowTotal: PossiblyMixedValue<string>
  factor: PossiblyMixedValue<string>
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

export const initialValues: SingleFormData = {
  name: '',
  type: '',
  basePrice: '',
  productionTimeFastTotal: '',
  productionTimeSlowTotal: '',
  factor: '',
}

export const createValidationSchemaWithDefaultProductionTimes = (
  defaultProductionTimes?: DefaultProductionTimes
) =>
  Yup.lazy((values: MixedFormData) =>
    toMixedValidationSchema({
      name: Yup.string().label('Name'),
      type: Yup.string().label('Production time type').required(),
      basePrice: Yup.number()
        .label('Base price')
        .min(0)
        .when(['factor'], {
          is: (factor: number) => typeof factor === 'undefined',
          then: schema => schema.required('Either base price or percentage is required'),
        }),
      productionTimeFastTotal: Yup.number()
        .label('Minimum total production time')
        .integer()
        .required()
        .when(['type'], {
          is: (type: string) => type === ProductionTimeOption.EXPEDITED,
          then: schema =>
            // Expedited Fast < Default Fast
            schema
              .min(1)
              .max(
                Math.min(
                  ...compact([
                    5,
                    defaultProductionTimes?.productionTimeFast
                      ? defaultProductionTimes.productionTimeFast - 1
                      : null,
                  ])
                )
              ),
        })
        .when(['type'], {
          is: (type: string) => type === ProductionTimeOption.ECONOMY,
          then: schema =>
            // Economy Fast > Default Fast
            schema
              .min(
                Math.max(
                  ...compact([
                    4,
                    defaultProductionTimes?.productionTimeFast
                      ? defaultProductionTimes.productionTimeFast + 1
                      : null,
                  ])
                )
              )
              .max(25),
        }),
      productionTimeSlowTotal: Yup.number()
        .label('Maximum total production time')
        .integer()
        .required()
        .min(Yup.ref('productionTimeFastTotal'))
        .when(['type'], {
          is: (type: string) => type === ProductionTimeOption.EXPEDITED,
          then: schema =>
            // Expedited Slow < Default Fast
            schema.max(
              Math.min(
                ...compact([
                  5,
                  defaultProductionTimes?.productionTimeFast
                    ? defaultProductionTimes.productionTimeFast - 1
                    : null,
                ])
              )
            ),
        })
        .when(['type'], {
          is: (type: string) => type === ProductionTimeOption.ECONOMY,
          then: schema =>
            schema
              // Economy Slow > Default Slow
              .min(
                Math.max(
                  ...compact([
                    Number(values.productionTimeFastTotal),
                    defaultProductionTimes?.productionTimeSlow
                      ? defaultProductionTimes.productionTimeSlow + 1
                      : null,
                  ])
                )
              )
              .max(25)
              .test(
                'interval',
                'Production time interval should be less than or equal to 10 business days',
                (value, ctx) => {
                  if (
                    value &&
                    ctx.parent.productionTimeFastTotal &&
                    value > ctx.parent.productionTimeFastTotal + 10
                  ) {
                    return ctx.createError({path: ctx.path})
                  }
                  return true
                }
              ),
        }),
      factor: Yup.number()
        .label('Percentage')
        .when(['basePrice'], {
          is: (basePrice: number) => !basePrice,
          then: schema => schema.required('Either base price or percentage is required'),
        })
        .when(['type'], {
          is: (type: string) => type === ProductionTimeOption.EXPEDITED,
          then: schema => schema.moreThan(0),
        })
        .when(['type'], {
          is: (type: string) => type === ProductionTimeOption.ECONOMY,
          then: schema => schema.min(-100).lessThan(0),
        }),
    })
  )

type Props = {
  formik: FormikProps<SingleFormData> | FormikProps<MixedFormData>
  provider: Provider
  linkedConfigsCount: number
}

const productionTimeOptions: ProductionTimeOptionOption[] = Object.values(ProductionTimeOption).map(
  option => ({
    label: capitalize(option),
    value: option,
  })
)

const ProductionTimesForm = ({formik, provider, linkedConfigsCount}: Props) => {
  useKeyPress(
    preventDoubleClick(() => formik.handleSubmit()),
    'Enter'
  )

  return (
    <form onSubmit={formik.handleSubmit}>
      {formik.errors.formikGeneral && <Paragraph error>{formik.errors.formikGeneral}</Paragraph>}
      <FormRow>
        <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.name}>
          <MixedValueInputWrapper
            locked={isMixedValue(formik.values.name)}
            onClick={() => formik.setFieldValue('name', initialValues.name)}
            unlockButtonIconTitle={unlockMixedValuesLabel}
          >
            <InputField
              label="Name (optional, for your internal reference only)"
              name="name"
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.name)}
              value={isMixedValue(formik.values.name) ? initialValues.name : formik.values.name}
              onChange={(value, name, event) => formik.handleChange(event)}
              onBlur={formik.handleBlur}
            />
          </MixedValueInputWrapper>
        </FieldNote>
      </FormRow>
      <FormRow>
        <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.type}>
          <MixedValueInputWrapper
            locked={isMixedValue(formik.values.type)}
            onClick={() => formik.setFieldValue('type', initialValues.type)}
            unlockButtonIconTitle={unlockMixedValuesLabel}
          >
            <SelectField
              name="type"
              placeholder="Select production type"
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.type)}
              value={productionTimeOptions.find(({value}) => value === formik.values.type)}
              menu={<SelectMenu values={productionTimeOptions} />}
              onChange={option => {
                formik.setFieldValue('type', (option as ProductionTimeOptionOption).value)
              }}
              onBlur={formik.handleBlur}
            />
          </MixedValueInputWrapper>
        </FieldNote>
      </FormRow>
      <Headline size="s" label="Total production time" classNames={['u-margin-top-l']} />
      {linkedConfigsCount > 0 && (
        <>
          <Paragraph minor size="s">
            The total production time is validated based on the default production time of linked
            materials.
          </Paragraph>
          {formik.values.type === ProductionTimeOption.ECONOMY &&
            formik.submitCount > 0 &&
            (formik.errors.productionTimeFastTotal || formik.errors.productionTimeSlowTotal) && (
              <Paragraph minor error size="s">
                An economy option should always have a greater total production time than the linked
                material configurations standard production times.
              </Paragraph>
            )}
        </>
      )}
      <MixedValueFormSection
        locked={
          isMixedValue(formik.values.productionTimeFastTotal) ||
          isMixedValue(formik.values.productionTimeSlowTotal)
        }
        onClick={() => {
          formik.setFieldValue(
            'productionTimeFastTotal',
            isMixedValue(formik.values.productionTimeFastTotal)
              ? initialValues.productionTimeFastTotal
              : formik.values.productionTimeFastTotal
          )
          formik.setFieldValue(
            'productionTimeSlowTotal',
            isMixedValue(formik.values.productionTimeSlowTotal)
              ? initialValues.productionTimeSlowTotal
              : formik.values.productionTimeSlowTotal
          )
        }}
        unlockButtonLabel={unlockMixedValuesLabel}
      >
        <FormRow layout="half-half" divider="-">
          <FieldNote
            type="error"
            note={formik.submitCount > 0 && formik.errors.productionTimeFastTotal}
          >
            <NumberInputField
              label="min"
              name="productionTimeFastTotal"
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.productionTimeFastTotal)}
              value={
                isMixedValue(formik.values.productionTimeFastTotal)
                  ? initialValues.productionTimeFastTotal
                  : formik.values.productionTimeFastTotal
              }
              suffix="business day(s)"
              error={formik.submitCount > 0 && !!formik.errors.productionTimeFastTotal}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
          <FieldNote
            type="error"
            note={formik.submitCount > 0 && formik.errors.productionTimeSlowTotal}
          >
            <NumberInputField
              label="max"
              name="productionTimeSlowTotal"
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.productionTimeSlowTotal)}
              value={
                isMixedValue(formik.values.productionTimeSlowTotal)
                  ? initialValues.productionTimeSlowTotal
                  : formik.values.productionTimeSlowTotal
              }
              suffix="business day(s)"
              error={formik.submitCount > 0 && !!formik.errors.productionTimeSlowTotal}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
        </FormRow>
      </MixedValueFormSection>
      <Headline size="s" label="Additional production cost" classNames={['u-margin-top-l']} />
      <Paragraph minor>
        We require the option to have either a percentage or a base price increase/decrease. These
        values can also be combined.
      </Paragraph>
      <Paragraph minor>
        <strong>Expedited option:</strong> The value entered in the base price or percentage field
        should have a positive number.
        <br />
        <strong>Economy option:</strong> The value entered in the percentage field should have a
        negative number, e.g. -10.
      </Paragraph>
      <FormRow>
        <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.factor}>
          <MixedValueInputWrapper
            locked={isMixedValue(formik.values.factor)}
            onClick={() => formik.setFieldValue('factor', initialValues.factor)}
            unlockButtonIconTitle={unlockMixedValuesLabel}
          >
            <NumberInputField
              allowDecimals
              allowOnly2Decimals
              label="Percentage"
              name="factor"
              suffix="%"
              error={formik.submitCount > 0 && !!formik.errors.factor}
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.factor)}
              value={
                isMixedValue(formik.values.factor) ? initialValues.factor : formik.values.factor
              }
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </MixedValueInputWrapper>
        </FieldNote>
      </FormRow>
      <FormRow>
        <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.basePrice}>
          <MixedValueInputWrapper
            locked={isMixedValue(formik.values.basePrice)}
            onClick={() => formik.setFieldValue('basePrice', initialValues.basePrice)}
            unlockButtonIconTitle={unlockMixedValuesLabel}
            disabled={isMixedValue(formik.values.type)}
          >
            <NumberInputField
              allowDecimals
              label="Base price per file"
              name="basePrice"
              suffix={formatCurrency(provider.currency)}
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.basePrice)}
              value={
                isMixedValue(formik.values.basePrice)
                  ? initialValues.basePrice
                  : formik.values.basePrice
              }
              error={formik.submitCount > 0 && !!formik.errors.basePrice}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
              disabled={formik.values.type === 'economy'}
            />
          </MixedValueInputWrapper>
        </FieldNote>
      </FormRow>
    </form>
  )
}

export default ProductionTimesForm
